import { useState } from 'react';
import { Link } from 'react-router-dom';

// Mock data for projects and clips
const MOCK_PROJECTS = [
  { id: '1', name: 'Logo Animations', clipCount: 8, updatedAt: '2 hours ago' },
  { id: '2', name: 'UI Transitions', clipCount: 12, updatedAt: '1 day ago' },
  { id: '3', name: 'Social Media', clipCount: 5, updatedAt: '3 days ago' },
];

const MOCK_CLIPS = [
  {
    id: 'clip-1',
    title: 'Logo Bounce Intro',
    projectId: '1',
    projectName: 'Logo Animations',
    duration: 3.2,
    status: 'completed' as const,
    tags: ['bounce', 'scale'],
    thumbnail: null,
  },
  {
    id: 'clip-2',
    title: 'Slide Transition',
    projectId: '2',
    projectName: 'UI Transitions',
    duration: 1.5,
    status: 'completed' as const,
    tags: ['slide', 'ease-out'],
    thumbnail: null,
  },
  {
    id: 'clip-3',
    title: 'Color Fade',
    projectId: '1',
    projectName: 'Logo Animations',
    duration: 2.8,
    status: 'processing' as const,
    tags: ['fade', 'color'],
    thumbnail: null,
  },
  {
    id: 'clip-4',
    title: 'Zoom Effect',
    projectId: '3',
    projectName: 'Social Media',
    duration: 4.0,
    status: 'completed' as const,
    tags: ['zoom', 'pan'],
    thumbnail: null,
  },
  {
    id: 'clip-5',
    title: 'Text Reveal',
    projectId: '2',
    projectName: 'UI Transitions',
    duration: 2.0,
    status: 'failed' as const,
    tags: ['text', 'reveal'],
    thumbnail: null,
  },
  {
    id: 'clip-6',
    title: 'Particle Effect',
    projectId: '3',
    projectName: 'Social Media',
    duration: 5.5,
    status: 'completed' as const,
    tags: ['particles', 'explosion'],
    thumbnail: null,
  },
];

type ViewMode = 'grid' | 'list';
type FilterStatus = 'all' | 'completed' | 'processing' | 'failed';

export default function DashboardPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Filter clips based on current filters
  const filteredClips = MOCK_CLIPS.filter((clip) => {
    if (filterStatus !== 'all' && clip.status !== filterStatus) return false;
    if (selectedProject && clip.projectId !== selectedProject) return false;
    if (searchQuery && !clip.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div
      style={{
        display: 'flex',
        minHeight: 'calc(100vh - 64px)',
      }}
    >
      {/* Sidebar */}
      <aside
        style={{
          width: 280,
          background: 'var(--black-800)',
          borderRight: '1px solid var(--black-700)',
          padding: 'var(--space-xl)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-xl)',
        }}
      >
        {/* New Upload Button */}
        <button
          onClick={() => setIsUploadModalOpen(true)}
          className="btn btn-primary"
          style={{ width: '100%' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          Upload Clip
        </button>

        {/* Projects List */}
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--space-md)',
            }}
          >
            <h4 style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--black-300)' }}>
              Projects
            </h4>
            <button
              style={{
                width: 24,
                height: 24,
                borderRadius: 'var(--radius-sm)',
                background: 'transparent',
                border: '1px solid var(--black-600)',
                color: 'var(--black-300)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all var(--transition-fast)',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
            <ProjectButton
              active={selectedProject === null}
              onClick={() => setSelectedProject(null)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
              All Clips
              <span style={{ marginLeft: 'auto', color: 'var(--black-400)', fontSize: '0.75rem' }}>
                {MOCK_CLIPS.length}
              </span>
            </ProjectButton>

            {MOCK_PROJECTS.map((project) => (
              <ProjectButton
                key={project.id}
                active={selectedProject === project.id}
                onClick={() => setSelectedProject(project.id)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                {project.name}
                <span style={{ marginLeft: 'auto', color: 'var(--black-400)', fontSize: '0.75rem' }}>
                  {project.clipCount}
                </span>
              </ProjectButton>
            ))}
          </div>
        </div>

        {/* Quick Filters */}
        <div>
          <h4
            style={{
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: 'var(--black-300)',
              marginBottom: 'var(--space-md)',
            }}
          >
            Filter by Status
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
            <FilterButton active={filterStatus === 'all'} onClick={() => setFilterStatus('all')}>
              All
            </FilterButton>
            <FilterButton active={filterStatus === 'completed'} onClick={() => setFilterStatus('completed')}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--cyan-500)',
                }}
              />
              Completed
            </FilterButton>
            <FilterButton active={filterStatus === 'processing'} onClick={() => setFilterStatus('processing')}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--amber-500)',
                }}
              />
              Processing
            </FilterButton>
            <FilterButton active={filterStatus === 'failed'} onClick={() => setFilterStatus('failed')}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--red-500)',
                }}
              />
              Failed
            </FilterButton>
          </div>
        </div>

        {/* Usage Stats */}
        <div
          style={{
            marginTop: 'auto',
            padding: 'var(--space-lg)',
            background: 'var(--black-700)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--black-600)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-sm)' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--black-200)' }}>Monthly Usage</span>
            <span style={{ fontSize: '0.875rem', color: 'white' }}>3/5</span>
          </div>
          <div className="progress-bar" style={{ marginBottom: 'var(--space-md)' }}>
            <div className="progress-fill" style={{ width: '60%' }} />
          </div>
          <Link
            to="#"
            style={{
              fontSize: '0.8125rem',
              color: 'var(--cyan-500)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-xs)',
            }}
          >
            Upgrade to Pro
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, padding: 'var(--space-xl)' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--space-xl)',
          }}
        >
          <div>
            <h2 style={{ marginBottom: 'var(--space-xs)' }}>
              {selectedProject
                ? MOCK_PROJECTS.find((p) => p.id === selectedProject)?.name
                : 'All Clips'}
            </h2>
            <p style={{ color: 'var(--black-300)', fontSize: '0.875rem' }}>
              {filteredClips.length} clips
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search clips..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input"
                style={{
                  width: 240,
                  paddingLeft: 40,
                  background: 'var(--black-800)',
                }}
              />
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--black-400)"
                strokeWidth="2"
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>

            {/* View Toggle */}
            <div
              style={{
                display: 'flex',
                padding: 4,
                background: 'var(--black-800)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--black-600)',
              }}
            >
              <button
                onClick={() => setViewMode('grid')}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--radius-sm)',
                  background: viewMode === 'grid' ? 'var(--black-600)' : 'transparent',
                  border: 'none',
                  color: viewMode === 'grid' ? 'white' : 'var(--black-400)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--radius-sm)',
                  background: viewMode === 'list' ? 'var(--black-600)' : 'transparent',
                  border: 'none',
                  color: viewMode === 'list' ? 'white' : 'var(--black-400)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Clips Grid/List */}
        {filteredClips.length === 0 ? (
          <EmptyState onUpload={() => setIsUploadModalOpen(true)} />
        ) : viewMode === 'grid' ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 'var(--space-lg)',
            }}
          >
            {filteredClips.map((clip, index) => (
              <ClipCard key={clip.id} clip={clip} index={index} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
            {filteredClips.map((clip, index) => (
              <ClipRow key={clip.id} clip={clip} index={index} />
            ))}
          </div>
        )}
      </main>

      {/* Upload Modal */}
      {isUploadModalOpen && <UploadModal onClose={() => setIsUploadModalOpen(false)} />}
    </div>
  );
}

function ProjectButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-sm)',
        width: '100%',
        padding: 'var(--space-sm) var(--space-md)',
        fontSize: '0.875rem',
        color: active ? 'white' : 'var(--black-200)',
        background: active ? 'var(--black-600)' : 'transparent',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all var(--transition-fast)',
      }}
    >
      {children}
    </button>
  );
}

function FilterButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-sm)',
        padding: 'var(--space-sm) var(--space-md)',
        fontSize: '0.8125rem',
        color: active ? 'white' : 'var(--black-300)',
        background: active ? 'var(--black-600)' : 'transparent',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        transition: 'all var(--transition-fast)',
      }}
    >
      {children}
    </button>
  );
}

function ClipCard({
  clip,
  index,
}: {
  clip: (typeof MOCK_CLIPS)[0];
  index: number;
}) {
  const statusColors = {
    completed: { bg: 'var(--cyan-glow)', border: 'rgba(6, 214, 160, 0.3)', text: 'var(--cyan-400)' },
    processing: { bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.3)', text: 'var(--amber-400)' },
    failed: { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.3)', text: 'var(--red-400)' },
  };

  const status = statusColors[clip.status];

  return (
    <Link
      to={`/editor/${clip.id}`}
      className="card card-interactive"
      style={{
        textDecoration: 'none',
        animation: 'slideUp 0.4s ease-out forwards',
        animationDelay: `${index * 0.05}s`,
        opacity: 0,
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          aspectRatio: '16/9',
          background: 'var(--black-700)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          borderBottom: '1px solid var(--black-600)',
        }}
      >
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--black-500)" strokeWidth="1.5">
          <polygon points="23 7 16 12 23 17 23 7" />
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        </svg>

        {/* Duration badge */}
        <div
          style={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            padding: '2px 6px',
            background: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.75rem',
            fontFamily: 'var(--font-mono)',
            color: 'white',
          }}
        >
          {clip.duration.toFixed(1)}s
        </div>

        {/* Processing indicator */}
        {clip.status === 'processing' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                border: '3px solid var(--black-600)',
                borderTopColor: 'var(--amber-500)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: 'var(--space-md)' }}>
        <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 'var(--space-sm)' }}>
          <div>
            <h4 style={{ fontSize: '0.9375rem', marginBottom: 'var(--space-xs)' }}>{clip.title}</h4>
            <p style={{ fontSize: '0.8125rem', color: 'var(--black-400)' }}>{clip.projectName}</p>
          </div>
          <span
            className="badge"
            style={{
              background: status.bg,
              color: status.text,
              border: `1px solid ${status.border}`,
              textTransform: 'capitalize',
            }}
          >
            {clip.status}
          </span>
        </div>

        {/* Tags */}
        <div style={{ display: 'flex', gap: 'var(--space-xs)', marginTop: 'var(--space-md)', flexWrap: 'wrap' }}>
          {clip.tags.map((tag) => (
            <span
              key={tag}
              style={{
                padding: '2px 8px',
                fontSize: '0.6875rem',
                color: 'var(--black-300)',
                background: 'var(--black-700)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

function ClipRow({
  clip,
  index,
}: {
  clip: (typeof MOCK_CLIPS)[0];
  index: number;
}) {
  const statusColors = {
    completed: { bg: 'var(--cyan-glow)', border: 'rgba(6, 214, 160, 0.3)', text: 'var(--cyan-400)' },
    processing: { bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.3)', text: 'var(--amber-400)' },
    failed: { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.3)', text: 'var(--red-400)' },
  };

  const status = statusColors[clip.status];

  return (
    <Link
      to={`/editor/${clip.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-lg)',
        padding: 'var(--space-md)',
        background: 'var(--black-800)',
        border: '1px solid var(--black-700)',
        borderRadius: 'var(--radius-md)',
        textDecoration: 'none',
        transition: 'all var(--transition-fast)',
        animation: 'slideUp 0.4s ease-out forwards',
        animationDelay: `${index * 0.03}s`,
        opacity: 0,
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          width: 120,
          aspectRatio: '16/9',
          background: 'var(--black-700)',
          borderRadius: 'var(--radius-sm)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--black-500)" strokeWidth="1.5">
          <polygon points="23 7 16 12 23 17 23 7" />
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        </svg>
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <h4 style={{ fontSize: '0.9375rem', marginBottom: 'var(--space-xs)' }}>{clip.title}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <span style={{ fontSize: '0.8125rem', color: 'var(--black-400)' }}>{clip.projectName}</span>
          <span style={{ fontSize: '0.8125rem', color: 'var(--black-400)', fontFamily: 'var(--font-mono)' }}>
            {clip.duration.toFixed(1)}s
          </span>
        </div>
      </div>

      {/* Tags */}
      <div style={{ display: 'flex', gap: 'var(--space-xs)' }}>
        {clip.tags.map((tag) => (
          <span
            key={tag}
            style={{
              padding: '2px 8px',
              fontSize: '0.6875rem',
              color: 'var(--black-300)',
              background: 'var(--black-700)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Status */}
      <span
        className="badge"
        style={{
          background: status.bg,
          color: status.text,
          border: `1px solid ${status.border}`,
          textTransform: 'capitalize',
        }}
      >
        {clip.status}
      </span>

      {/* Arrow */}
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--black-400)" strokeWidth="2">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </Link>
  );
}

function EmptyState({ onUpload }: { onUpload: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-4xl)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'var(--black-800)',
          border: '1px solid var(--black-600)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 'var(--space-xl)',
        }}
      >
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--black-400)" strokeWidth="1.5">
          <polygon points="23 7 16 12 23 17 23 7" />
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        </svg>
      </div>
      <h3 style={{ marginBottom: 'var(--space-sm)' }}>No clips yet</h3>
      <p style={{ color: 'var(--black-300)', marginBottom: 'var(--space-xl)', maxWidth: 400 }}>
        Upload your first video clip to start generating Remotion animation code.
      </p>
      <button onClick={onUpload} className="btn btn-primary">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        Upload Clip
      </button>
    </div>
  );
}

function UploadModal({ onClose }: { onClose: () => void }) {
  const [isDragging, setIsDragging] = useState(false);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-xl)',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(4px)',
        }}
      />

      {/* Modal */}
      <div
        className="animate-slide-up"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 560,
          background: 'var(--black-800)',
          border: '1px solid var(--black-600)',
          borderRadius: 'var(--radius-xl)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-lg) var(--space-xl)',
            borderBottom: '1px solid var(--black-700)',
          }}
        >
          <h3>Upload Video Clip</h3>
          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-sm)',
              background: 'transparent',
              border: 'none',
              color: 'var(--black-300)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 'var(--space-xl)' }}>
          {/* Drop Zone */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              // Handle file drop
            }}
            style={{
              padding: 'var(--space-3xl)',
              border: `2px dashed ${isDragging ? 'var(--cyan-500)' : 'var(--black-500)'}`,
              borderRadius: 'var(--radius-lg)',
              background: isDragging ? 'var(--cyan-glow)' : 'var(--black-700)',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all var(--transition-fast)',
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                margin: '0 auto var(--space-lg)',
                borderRadius: 'var(--radius-lg)',
                background: 'var(--black-600)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke={isDragging ? 'var(--cyan-500)' : 'var(--black-300)'}
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <p style={{ color: 'white', marginBottom: 'var(--space-xs)' }}>
              Drag and drop your video here
            </p>
            <p style={{ color: 'var(--black-400)', fontSize: '0.875rem' }}>
              or click to browse
            </p>
            <p
              style={{
                marginTop: 'var(--space-md)',
                fontSize: '0.75rem',
                color: 'var(--black-400)',
              }}
            >
              MP4, MOV, WebM • Max 100MB • Max 60 seconds
            </p>
          </div>

          {/* Project Select */}
          <div style={{ marginTop: 'var(--space-xl)' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                color: 'var(--black-200)',
                marginBottom: 'var(--space-sm)',
              }}
            >
              Add to project (optional)
            </label>
            <select
              className="input"
              style={{
                appearance: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="">No project</option>
              {MOCK_PROJECTS.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-md)',
            padding: 'var(--space-lg) var(--space-xl)',
            borderTop: '1px solid var(--black-700)',
            background: 'var(--black-700)',
          }}
        >
          <button onClick={onClose} className="btn btn-ghost">
            Cancel
          </button>
          <button className="btn btn-primary" disabled>
            Upload & Analyze
          </button>
        </div>
      </div>
    </div>
  );
}

// Add keyframes for spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);
